import React from 'react';
import { ContextualChatButton } from './ContextualChatButton';

interface GPSData {
  latitude: number;
  longitude: number;
  altitude?: number;
  accuracy: number;
  timestamp: number;
}

interface WorkoutData {
  id: string;
  workoutId: string;
  type: 'strength' | 'cardio' | 'mobility';
  startTime: Date;
  endTime?: Date;
  totalDuration: number;
  exercises?: {
    exerciseId: string;
    duration: number;
    completed: boolean;
    sets?: number;
    reps?: number;
  }[];
  gpsData?: GPSData[];
  distance?: number;
  avgPace?: number;
  maxPace?: number;
  calories?: number;
  heartRate?: {
    avg?: number;
    max?: number;
  };
  notes?: string;
  progressInsights?: string[];
  shouldCelebrate?: boolean;
  comparisonData?: {
    improvement: string;
    metric: string;
    percentageChange: number;
    timeBetween: string;
  };
  debriefInvitation?: {
    activity_id: string;
    activity_title: string;
    activity_type: string;
    invitation_message: string;
    chat_context: {
      type: string;
      id: string;
      title: string;
      action: string;
    };
  };
  cedarResponse?: string;
}

interface WorkoutSessionResultsProps {
  workoutData: WorkoutData;
  workoutTitle: string;
  onClose: () => void;
  onViewActivity?: () => void;
}

export const WorkoutSessionResults: React.FC<WorkoutSessionResultsProps> = ({
  workoutData,
  workoutTitle,
  onClose,
  onViewActivity
}) => {
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPace = (pace: number) => {
    if (pace === 0 || !isFinite(pace)) return '--:--';
    const mins = Math.floor(pace / 60);
    const secs = Math.floor(pace % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const completedExercises = workoutData.exercises?.filter(ex => ex.completed).length || 0;
  const totalExercises = workoutData.exercises?.length || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Workout Complete!</h2>
              <p className="text-sm text-gray-600 mt-1">{workoutTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Success Icon */}
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-600">
            {formatDate(workoutData.startTime)}
          </p>
        </div>

        {/* Main Stats */}
        <div className="px-6 pb-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {formatTime(workoutData.totalDuration)}
              </div>
              <div className="text-sm text-blue-800">Total Time</div>
            </div>
            
            {workoutData.type === 'cardio' ? (
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {workoutData.distance?.toFixed(2) || '0.00'}
                </div>
                <div className="text-sm text-green-800">Distance (km)</div>
              </div>
            ) : (
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {completedExercises}/{totalExercises}
                </div>
                <div className="text-sm text-purple-800">Exercises</div>
              </div>
            )}
          </div>

          {/* Cardio-specific stats */}
          {workoutData.type === 'cardio' && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-gray-700">
                  {formatPace(workoutData.avgPace || 0)}
                </div>
                <div className="text-xs text-gray-600">Avg Pace (/km)</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-gray-700">
                  {workoutData.calories || '--'}
                </div>
                <div className="text-xs text-gray-600">Calories</div>
              </div>
            </div>
          )}

          {/* Exercise breakdown for strength/mobility */}
          {workoutData.type !== 'cardio' && workoutData.exercises && workoutData.exercises.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Exercise Breakdown</h3>
              <div className="space-y-2">
                {workoutData.exercises.map((exercise, index) => (
                  <div key={exercise.exerciseId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        exercise.completed ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <span className="text-sm font-medium text-gray-700">
                        Exercise {index + 1}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatTime(exercise.duration)}
                      </div>
                      {exercise.sets && (
                        <div className="text-xs text-gray-600">
                          {exercise.sets} sets × {exercise.reps} reps
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* GPS Info for cardio */}
          {workoutData.type === 'cardio' && workoutData.gpsData && workoutData.gpsData.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-800 mb-2">GPS Tracking</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-600">Data Points:</span>
                  <span className="ml-2 font-medium">{workoutData.gpsData.length}</span>
                </div>
                <div>
                  <span className="text-blue-600">Avg Accuracy:</span>
                  <span className="ml-2 font-medium">
                    {Math.round(workoutData.gpsData.reduce((sum, point) => sum + point.accuracy, 0) / workoutData.gpsData.length)}m
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Session Summary */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Session Complete!</h3>
              {workoutData.shouldCelebrate && (
                <div className="flex items-center space-x-2 bg-green-50 px-3 py-1 rounded-full">
                  <span className="text-lg">🎉</span>
                  <span className="text-sm font-medium text-green-700">Progress Made!</span>
                </div>
              )}
            </div>

            {/* Progress Insights - Cedar's contextual progress recognition */}
            {workoutData.progressInsights && workoutData.progressInsights.length > 0 && (
              <div className="mb-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 text-lg">📈</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-green-900 mb-2">Cedar's Progress Analysis</h4>
                    <div className="space-y-2">
                      {workoutData.progressInsights.map((insight: string, index: number) => (
                        <p key={index} className="text-sm text-green-800 leading-relaxed">
                          {insight}
                        </p>
                      ))}
                    </div>
                    {workoutData.comparisonData && (
                      <div className="mt-3 text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                        <strong>{workoutData.comparisonData.improvement}</strong> compared to {workoutData.comparisonData.timeBetween} ago
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 🌟 CEDAR DEBRIEF INVITATION - Appears after timer-based sessions */}
            {workoutData.debriefInvitation && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-lg">🌲</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-900">Cedar wants to debrief!</h4>
                      <p className="text-sm text-green-700">
                        {workoutData.debriefInvitation.invitation_message}
                      </p>
                    </div>
                  </div>
                  <ContextualChatButton
                    context={{
                      type: workoutData.debriefInvitation.chat_context.type as any,
                      id: workoutData.debriefInvitation.chat_context.id,
                      title: workoutData.debriefInvitation.chat_context.title,
                      action: workoutData.debriefInvitation.chat_context.action as any
                    }}
                    customMessage={`I want to talk about my ${workoutData.debriefInvitation.activity_title}. How did it go? Let me tell you!`}
                    text=""
                    variant="primary"
                    size="medium"
                  />
                </div>
              </div>
            )}

          <div className="space-y-3">
            {onViewActivity && workoutData.type === 'cardio' && (
              <button
                onClick={onViewActivity}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                View Activity Details
              </button>
            )}
            
            <button
              onClick={onClose}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            >
              Done
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 